import { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery } from '@redux-saga/core/effects';
import {
  AngebotsId,
  DoRequestResponse,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  SOFORTSCHUTZ_ACTIVE_REQUEST,
  VorversicherungAblaufdatumChanged
} from '@ps20-frontend/shop-integration-redux';
import { SofortschutzActiveRequest, SofortschutzActiveResponse } from '@ps20/bff-models';
import { ANZAHL_TAGE_TIER_ZU_JUNG, App, isTierZuJungByToday } from '@ps20-frontend/frontend-models';
import { PS20Logger } from '@ps20-frontend/logging';
import dayjs from 'dayjs';
import { safeSaga } from '@ps20-frontend/safe-saga';
import {
  convertDateFormatFromLocalStateToShortIso,
  DATEFORMAT_LOCAL_STATE,
  DATEFORMAT_SHORT_ISO
} from '@ps20-frontend/utils';
import { CheckoutActions } from '../checkout.actions';
import { IAppState } from '../localStore';
import { Produkt, TkvCheckoutProdukt, TkvV2CheckoutProdukt } from '../model';
import { Vorversicherung } from '../model/vorversicherung';
import { getProdukteByRisikoort, getRisikoort } from '../selector/selector.util';
import { getMaxAblaufdatum, isExpirationMoreThan18MonthsAway } from '../selector/beginnAblaufdatum.selector';
import { hasGefahrenInVorversicherung } from '../selector/vorversicherung.selector';

export function* watchUpdateSofortschutzActive(): SagaIterator {
  yield takeEvery(
    GlobalActions.UPDATE_SOFORTSCHUTZ_ACTIVE,
    safeSaga(callSofortschutzActive, App.SHOP_CHECKOUT)
  );
  yield takeEvery(
    SOFORTSCHUTZ_ACTIVE_REQUEST.SOFORTSCHUTZ_ACTIVE_REQUEST_SUCCESSFUL,
    safeSaga(successCallback, App.SHOP_CHECKOUT)
  );
  yield takeEvery(
    GlobalActions.VORVERSICHERUNG_ABLAUFDATUM_CHANGED,
    safeSaga(handleVorversicherungAblaufdatumChanged, App.SHOP_CHECKOUT)
  );
}

export function* callSofortschutzActive(action: GlobalAction<AngebotsId>): SagaIterator {
  const url = '/privatschutz/api/sofortschutzActive';
  const state: IAppState = yield select();

  const relevantesAngebot: AngebotsId = action.payload;
  const relevantProdukt: Produkt = getRelevantProduktForAngebot(state, relevantesAngebot);
  if (!relevantProdukt) {
    return yield call(
      GlobalPut.getInstance().put,
      new GlobalActions().updateSofortschutzActiveFinished(),
    );
  }
  if (hasGefahrenInVorversicherung(relevantProdukt, state.vorversicherungen)) {
    return yield call(
      GlobalPut.getInstance().put,
      new GlobalActions().updateSofortschutzActiveFinished()
    );
  }
  const relevanteVorversicherungen: Vorversicherung = getVorversicherungenForAngebot(
    state,
    relevantesAngebot
  );
  const ablaufdatenVorversicherungen: Array<string> = getAblaufdatenOfVorversicherungen(
    relevanteVorversicherungen
  );

  if (
    ablaufdatenVorversicherungen === null ||
    ablaufdatenVorversicherungen.length === 0 ||
    !relevantProdukt.sofortschutzAvailable
  ) {
    return yield call(
      GlobalPut.getInstance().put,
      new GlobalActions().updateSofortschutzActiveFinished()
    );
  }

  const wirksamkeitsbeginn = getWirksamkeitsbeginnForTkvIfApplicable(relevantProdukt);

  const sofortschutzActiveRequest: SofortschutzActiveRequest = {
    beginndatumVersicherung: convertDateFormatFromLocalStateToShortIso(relevantProdukt.beginn),
    ablaufdatenVorversicherungen,
    ...(wirksamkeitsbeginn && { wirksamkeitsbeginn })
  };

  const globalPut = GlobalPut.getInstance();
  yield call(
    globalPut.put.bind(globalPut),
    new GlobalActions().doRequest({
      body: sofortschutzActiveRequest,
      url,
      appId: relevantesAngebot.appId,
      angebotsId: relevantesAngebot.angebotsId,
      successEvent: SOFORTSCHUTZ_ACTIVE_REQUEST.SOFORTSCHUTZ_ACTIVE_REQUEST_SUCCESSFUL,
      errorEvent: SOFORTSCHUTZ_ACTIVE_REQUEST.SOFORTSCHUTZ_ACTIVE_REQUEST_FAILED
    })
  );
}

export function* successCallback(action: DoRequestResponse<SofortschutzActiveResponse>): SagaIterator {
  const appId: App = action.doRequestAction.payload.appId;
  const angebotsId = action.doRequestAction.payload.angebotsId;

  yield put(
    new CheckoutActions().sofortschutzActiveReceived({
      appId: appId,
      angebotsId,
      beginndatumChangeable: action.payload.beginndatumChangeable,
      sofortschutzActive: action.payload.sofortschutzActive,
      sofortschutzEndeDatum: action.payload.sofortschutzEndeDatum
    })
  );

  if (action.payload.sofortschutzActive && !action.payload.beginndatumChangeable) {
    const state: IAppState = yield select();
    const relevantesAngebot: AngebotsId = { appId, angebotsId };
    const relevantProdukt: Produkt = getRelevantProduktForAngebot(state, relevantesAngebot);
    if (!relevantProdukt) {
      return yield call(
        GlobalPut.getInstance().put,
        new GlobalActions().updateSofortschutzActiveFinished(),
      );
    }
    const relevanteVorversicherungen: Vorversicherung = getVorversicherungenForAngebot(
      state,
      relevantesAngebot
    );

    const vorversicherungsItems =
      relevanteVorversicherungen?.vorversicherungsListe || [];
    const maxAblaufdatum = getMaxAblaufdatum(vorversicherungsItems);
    const isExpirationMoreThan18Months = isExpirationMoreThan18MonthsAway(
      maxAblaufdatum,
      relevantProdukt
    );

    if (!isExpirationMoreThan18Months) {
      const wirksamkeitsbeginn = getWirksamkeitsbeginnForTkvIfApplicable(relevantProdukt);
      const beginnDatum = wirksamkeitsbeginn
        ? dayjs(wirksamkeitsbeginn, DATEFORMAT_SHORT_ISO).format(DATEFORMAT_LOCAL_STATE)
        : dayjs().add(1, 'day').format(DATEFORMAT_LOCAL_STATE);

      yield call(
        GlobalPut.getInstance().put,
        new GlobalActions().updateBeginnAblauf({
          appId,
          beginn: beginnDatum,
          angebotsId
        })
      );
    }
  }

  yield call(GlobalPut.getInstance().put, new GlobalActions().updateSofortschutzActiveFinished());
}

export function* handleVorversicherungAblaufdatumChanged(
  action: GlobalAction<VorversicherungAblaufdatumChanged>
): SagaIterator {
  const { appId, angebotsId, ablaufdatum } = action.payload;

  if (!ablaufdatum) {
    return yield call(
      GlobalPut.getInstance().put,
      new GlobalActions().updateSofortschutzActiveFinished()
    );
  }

  const state: IAppState = yield select();
  const relevantProdukt = getRelevantProduktForAngebot(state, { appId, angebotsId });
  if (hasGefahrenInVorversicherung(relevantProdukt, state.vorversicherungen)) {
    return yield call(
      GlobalPut.getInstance().put,
      new GlobalActions().updateSofortschutzActiveFinished()
    );
  }
  if (!relevantProdukt?.sofortschutzAvailable) {
    return yield call(
      GlobalPut.getInstance().put,
      new GlobalActions().updateSofortschutzActiveFinished()
    );
  }

  const relevanteVorversicherungen = getVorversicherungenForAngebot(state, { appId, angebotsId });
  const ablaufdatenVorversicherungen = getAblaufdatenOfVorversicherungen(relevanteVorversicherungen);

  if (!ablaufdatenVorversicherungen || ablaufdatenVorversicherungen.length === 0) {
    return yield call(
      GlobalPut.getInstance().put,
      new GlobalActions().updateSofortschutzActiveFinished()
    );
  }

  const wirksamkeitsbeginn = getWirksamkeitsbeginnForTkvIfApplicable(relevantProdukt);
  const beginndatumVersicherung = wirksamkeitsbeginn || dayjs().add(1, 'day').format(DATEFORMAT_SHORT_ISO);

  const sofortschutzActiveRequest: SofortschutzActiveRequest = {
    beginndatumVersicherung,
    ablaufdatenVorversicherungen,
    ...(wirksamkeitsbeginn && { wirksamkeitsbeginn })
  };

  const url = '/privatschutz/api/sofortschutzActive';
  const globalPut = GlobalPut.getInstance();
  yield call(
    globalPut.put.bind(globalPut),
    new GlobalActions().doRequest({
      body: sofortschutzActiveRequest,
      url,
      appId,
      angebotsId,
      successEvent: SOFORTSCHUTZ_ACTIVE_REQUEST.SOFORTSCHUTZ_ACTIVE_REQUEST_SUCCESSFUL,
      errorEvent: SOFORTSCHUTZ_ACTIVE_REQUEST.SOFORTSCHUTZ_ACTIVE_REQUEST_FAILED
    })
  );
}


function getRelevantProduktForAngebot(state: IAppState, angebot: AngebotsId) {
  const produkt: Produkt = state.produkte.find(
    value => value.appId === angebot.appId && value.angebotsId === angebot.angebotsId
  );
  if (produkt != null) {
    return produkt;
  }
  PS20Logger.info(
    App.SHOP_CHECKOUT,
    'Could not find Produkt',
    'SofortschutzActiveSaga.getRelevantProduktOfApp()'
  );
}

function getVorversicherungenForAngebot(state: IAppState, angebot: AngebotsId) {
  const unfRisikoort = getRisikoort(App.UNF, angebot.angebotsId, state.produkte);
  const unfProdukte = getProdukteByRisikoort(App.UNF, unfRisikoort, state.produkte);

  return state.vorversicherungen.find(
    vorvers =>
      (vorvers.appId !== App.UNF &&
        vorvers.appId === angebot.appId &&
        vorvers.angebotsId === angebot.angebotsId) ||
      (vorvers.appId === App.UNF &&
        vorvers.appId === angebot.appId &&
        unfProdukte.some(prod => vorvers.angebotsId === prod.angebotsId))
  );
}

function getAblaufdatenOfVorversicherungen(
  relevanteVorversicherung: Vorversicherung
): Array<string> {
  if (relevanteVorversicherung == null) {
    return [];
  }
  return relevanteVorversicherung.vorversicherungsListe
    .filter(value => value.ablaufdatum != null)
    .map(value => dayjs(value.ablaufdatum, DATEFORMAT_LOCAL_STATE))
    .filter(value => value.isValid())
    .map(value => value.format(DATEFORMAT_SHORT_ISO));
}

function getWirksamkeitsbeginnForTkvIfApplicable(produkt: Produkt): string | undefined {
  if (produkt.appId === App.TKV) {
    const tkvProdukt = produkt as TkvCheckoutProdukt;
    if (isTierZuJungByToday(tkvProdukt.geburtsdatumTier, DATEFORMAT_LOCAL_STATE)) {
      return dayjs(tkvProdukt.geburtsdatumTier, DATEFORMAT_LOCAL_STATE)
        .add(ANZAHL_TAGE_TIER_ZU_JUNG, 'd')
        .format(DATEFORMAT_SHORT_ISO);
    }
  } else if (produkt.appId === App.TKV_V2) {
    const tkvV2Produkt = produkt as TkvV2CheckoutProdukt;
    if (isTierZuJungByToday(tkvV2Produkt.animalBirthdate, DATEFORMAT_LOCAL_STATE)) {
      return dayjs(tkvV2Produkt.animalBirthdate, DATEFORMAT_LOCAL_STATE)
        .add(ANZAHL_TAGE_TIER_ZU_JUNG, 'd')
        .format(DATEFORMAT_SHORT_ISO);
    }
  }

  return undefined;
}
